import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { LocationStrategy } from '@angular/common';
import { BaseComponent } from '../../../../models/base/base-component';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { BaseInfoItem } from '../../../profile/models/base-info-item';
import { map } from 'rxjs/operators';
import { TrackByUtils } from '../../../../utils/track-by-utils';

@Component({
  selector: 'app-static-info-container',
  templateUrl: './static-info-container.component.html',
  styleUrls: ['./static-info-container.component.scss']
})
export class StaticInfoContainerComponent extends BaseComponent implements OnChanges {
  // All strings must be pre-localized.
  @Input() items!: BaseInfoItem[];
  @Input() title!: string;
  @Input() lastSection: boolean = false;
  @Input() hideBottomDivider: boolean = false;
  @Input() actionButtonText = $localize`Edit`;
  @Input() hideActionButton = false;
  @Input() disableActionButton = false;
  @Input() separateFirstItem = true;
  @Input() twoColLayout = false;

  @Output() actionButtonClicked = new EventEmitter<void>();

  private locationStrategy = inject(LocationStrategy);

  public trackByIndex = TrackByUtils.trackByIndex;

  private _title = new BehaviorSubject<string>(this.title);
  public title$ = this._title.asObservable();

  private _items = new BehaviorSubject<BaseInfoItem[]>([]);
  public items$ = this._items.asObservable();

  // The untrimmed items input. `items$` gets the first entry sliced off when there is no
  // title, but `#noTitleTemplate` renders from the raw input, so the grid column count
  // must be derived from this to stay in sync.
  private _rawItems = new BehaviorSubject<BaseInfoItem[]>([]);
  public rawItems$ = this._rawItems.asObservable();

  private _showFullSin = new BehaviorSubject<boolean>(false);
  public showFullSin$ = this._showFullSin.asObservable();

  private _separateFirstItem = new BehaviorSubject<boolean>(this.separateFirstItem);
  public separateFirstItem$ = this._separateFirstItem.asObservable();

  public sinButtonIcon$ = this.showFullSin$.pipe(
    map(show => {
      return show ? 'assets/icons/dark/Eye On.svg' : 'assets/icons/dark/Eye Off.svg';
    })
  );

  // Column tracks shared by the separated first item and the bottom items so their
  // underlines line up. When the first item is separated it is excluded from the count,
  // leaving the bottom row to define the columns (e.g. 3 items -> `repeat(3, 1fr)`).
  public gridTemplateColumns$ = combineLatest([this.rawItems$, this.separateFirstItem$]).pipe(
    map(([items, separateFirstItem]) => {
      const columns = Math.max((items?.length ?? 0) - (separateFirstItem ? 1 : 0), 1);
      return `repeat(${columns}, 1fr)`;
    })
  );

  setupBindings(): void {}

  setupViews(): void {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.title) this._title.next(this.title);
    if (changes.separateFirstItem) this._separateFirstItem.next(this.separateFirstItem);
    if (changes.items) {
      this._rawItems.next(this.items);
      this._items.next(this.items);
      this.title$.once(title => this.trimItemsArray(title));
    }
  }

  private trimItemsArray(title: string): void {
    this.items$.once(items => {
      if (!title) {
        this._items.next(items.slice(1));
      }
    });
  }

  public toggleSin(ev: MouseEvent): void {
    ev.preventDefault();
    this.showFullSin$.once(showFullSin => this._showFullSin.next(!showFullSin));
  }

  public navigateToLink(url: string | undefined): void {
    if (!url) return;
    if (!/^https?:\/\//i.test(url.trim())) return;
    const externalUrl = this.locationStrategy.prepareExternalUrl(url);
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  }
}
